/**
 * @fileoverview ClubOS User Profile Page
 * Shows the signed-in user's identity, role, permissions, and account details.
 */

import { useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftAvatar from "components/SoftAvatar";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useAuth } from "context/AuthContext";
import { ROLE_PERMISSIONS, ROLE_META, PERMISSIONS } from "constants/roles";
import { CLUB_NAME } from "utils/indiaLocale";
import { userService } from "api";

import curved0 from "assets/images/curved-images/curved0.jpg";

// ── Permission section definitions ──────────────────────────────────────────
const PERMISSION_SECTIONS = [
  {
    label: "Finance",
    icon: "account_balance",
    perms: [
      { key: PERMISSIONS.FINANCE_VIEW,     label: "View Finance" },
      { key: PERMISSIONS.FINANCE_EDIT,     label: "Edit Finance" },
      { key: PERMISSIONS.FINANCE_ADMIN,    label: "Finance Admin" },
      { key: PERMISSIONS.SALARY_VIEW,      label: "View Salaries" },
      { key: PERMISSIONS.SALARY_EDIT,      label: "Edit Salaries" },
      { key: PERMISSIONS.TRANSFER_VIEW,    label: "View Transfers" },
      { key: PERMISSIONS.TRANSFER_EDIT,    label: "Edit Transfers" },
      { key: PERMISSIONS.TRANSFER_APPROVE, label: "Approve Transfers" },
      { key: PERMISSIONS.BUDGET_VIEW,      label: "View Budget" },
      { key: PERMISSIONS.BUDGET_EDIT,      label: "Edit Budget" },
      { key: PERMISSIONS.REVENUE_VIEW,     label: "View Revenue" },
      { key: PERMISSIONS.REVENUE_EDIT,     label: "Edit Revenue" },
    ],
  },
  {
    label: "Squad",
    icon: "groups",
    perms: [
      { key: PERMISSIONS.SQUAD_VIEW,  label: "View Squad" },
      { key: PERMISSIONS.SQUAD_EDIT,  label: "Edit Squad" },
      { key: PERMISSIONS.SQUAD_ADMIN, label: "Squad Admin" },
    ],
  },
  {
    label: "Coaching",
    icon: "sports_soccer",
    perms: [
      { key: PERMISSIONS.COACHING_VIEW,  label: "View Coaching" },
      { key: PERMISSIONS.COACHING_EDIT,  label: "Edit Coaching" },
      { key: PERMISSIONS.COACHING_ADMIN, label: "Coaching Admin" },
    ],
  },
  {
    label: "Scouting",
    icon: "radar",
    perms: [
      { key: PERMISSIONS.SCOUTING_VIEW,  label: "View Scouting" },
      { key: PERMISSIONS.SCOUTING_EDIT,  label: "Edit Scouting" },
      { key: PERMISSIONS.SCOUTING_ADMIN, label: "Scouting Admin" },
    ],
  },
  {
    label: "Users & Reports",
    icon: "manage_accounts",
    perms: [
      { key: PERMISSIONS.USERS_VIEW,    label: "View Users" },
      { key: PERMISSIONS.USERS_EDIT,    label: "Edit Users" },
      { key: PERMISSIONS.USERS_ADMIN,   label: "Users Admin" },
      { key: PERMISSIONS.REPORTS_VIEW,  label: "View Reports" },
      { key: PERMISSIONS.REPORTS_EXPORT,label: "Export Reports" },
    ],
  },
];

// ── Info row ─────────────────────────────────────────────────────────────────
function InfoRow({ icon, label, value }) {
  return (
    <SoftBox display="flex" alignItems="center" py={1}>
      <SoftBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={36}
        height={36}
        borderRadius="lg"
        sx={{ backgroundColor: "rgba(26,115,232,0.08)", mr: 1.5, flexShrink: 0 }}
      >
        <Icon sx={{ color: "info.main", fontSize: "1rem !important" }}>{icon}</Icon>
      </SoftBox>
      <SoftBox flex={1}>
        <SoftTypography variant="caption" color="text" display="block" lineHeight={1.2}>
          {label}
        </SoftTypography>
        <SoftTypography variant="button" fontWeight="medium" color="dark">
          {value || "—"}
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}

InfoRow.propTypes = {
  icon:  PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};
InfoRow.defaultProps = { value: "—" };

// ── Component ─────────────────────────────────────────────────────────────────
function ProfilePage() {
  const { currentUser } = useAuth();

  // Change password dialog state
  const [pwdOpen, setPwdOpen]       = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd]         = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [otp, setOtp]               = useState("");
  const [otpSent, setOtpSent]       = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [pwdError, setPwdError]     = useState("");
  const [pwdSuccess, setPwdSuccess] = useState(false);
  const [pwdSaving, setPwdSaving]   = useState(false);

  const openPwdDialog = () => {
    setCurrentPwd(""); setNewPwd(""); setConfirmPwd(""); setOtp("");
    setPwdError(""); setPwdSuccess(false); setOtpSent(false); setPwdOpen(true);
  };

  const handleSendOtp = async () => {
    if (!currentPwd)           { setPwdError("Current password is required."); return; }
    if (newPwd.length < 8)     { setPwdError("New password must be at least 8 characters."); return; }
    if (newPwd !== confirmPwd) { setPwdError("Passwords do not match."); return; }
    setOtpSending(true); setPwdError("");
    try {
      await userService.sendOtp(currentUser.email);
      setOtpSent(true);
    } catch (err) {
      setPwdError(err?.detail ?? err?.title ?? "Failed to send OTP.");
    } finally { setOtpSending(false); }
  };

  const handleChangePassword = async () => {
    if (!otp) { setPwdError("Please enter the OTP sent to your email."); return; }
    setPwdSaving(true); setPwdError("");
    try {
      await userService.changePassword(currentPwd, newPwd, otp);
      setPwdSuccess(true);
      setTimeout(() => { setPwdOpen(false); setPwdSuccess(false); }, 1500);
    } catch (err) {
      setPwdError(err?.detail ?? err?.errors?.detail ?? err?.title ?? "Failed to change password.");
    } finally { setPwdSaving(false); }
  };

  if (!currentUser) return null;

  const userPerms = ROLE_PERMISSIONS[currentUser.role] ?? [];
  const roleMeta  = ROLE_META[currentUser.role] ?? { color: "info", icon: "person", description: "" };
  const grantedCount = userPerms.length;
  const totalCount   = Object.values(PERMISSIONS).length;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftBox mb={5}>
        {/* ── Hero cover ──────────────────────────────────────────────────── */}
        <SoftBox
          minHeight="12rem"
          borderRadius="xl"
          sx={{
            backgroundImage: ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
              `${linearGradient(
                rgba(gradients.info.main, 0.55),
                rgba(gradients.info.state, 0.55)
              )}, url(${curved0})`,
            backgroundSize: "cover",
            backgroundPosition: "50%",
          }}
        />

        {/* ── Profile card ────────────────────────────────────────────────── */}
        <Card
          sx={{
            mx: { xs: 1, md: 3 },
            mt: -8,
            mb: 3,
            backdropFilter: "saturate(200%) blur(30px)",
            backgroundColor: ({ functions: { rgba }, palette: { white } }) => rgba(white.main, 0.8),
            boxShadow: ({ boxShadows: { navbarBoxShadow } }) => navbarBoxShadow,
          }}
        >
          <SoftBox p={3}>
            <Grid container spacing={2} alignItems="center">
              {/* Avatar */}
              <Grid item>
                <SoftAvatar
                  variant="rounded"
                  size="xl"
                  shadow="sm"
                  sx={{ bgcolor: "info.main", fontSize: "1.6rem", width: 72, height: 72 }}
                >
                  {currentUser.avatar}
                </SoftAvatar>
              </Grid>

              {/* Name + role */}
              <Grid item xs>
                <SoftTypography variant="h4" fontWeight="bold">
                  {currentUser.name}
                </SoftTypography>
                <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
                  <SoftBadge
                    badgeContent={currentUser.role}
                    color={roleMeta.color}
                    size="sm"
                    container
                  />
                  <SoftTypography variant="caption" color="text">
                    {currentUser.department}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text">·</SoftTypography>
                  <SoftTypography variant="caption" color="text">
                    {CLUB_NAME}
                  </SoftTypography>
                </SoftBox>
              </Grid>

              {/* Access level chip */}
              <Grid item>
                <SoftBox textAlign="right">
                  <Chip
                    icon={<Icon sx={{ fontSize: "1rem !important" }}>{roleMeta.icon}</Icon>}
                    label={`${grantedCount} / ${totalCount} permissions`}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(26,115,232,0.1)",
                      color: "info.main",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                    }}
                  />
                  <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                    {roleMeta.description}
                  </SoftTypography>
                </SoftBox>
              </Grid>
            </Grid>
          </SoftBox>
        </Card>

        {/* ── Content grid ────────────────────────────────────────────────── */}
        <Grid container spacing={3} px={{ xs: 1, md: 3 }}>
          {/* Account details */}
          <Grid item xs={12} md={5} lg={4}>
            <Card sx={{ height: "100%" }}>
              <SoftBox p={3}>
                <SoftTypography variant="h6" fontWeight="bold" mb={2}>
                  Account Details
                </SoftTypography>
                <InfoRow icon="person"         label="Full Name"   value={currentUser.name} />
                <Divider sx={{ my: 0.5 }} />
                <InfoRow icon="email"           label="Email"       value={currentUser.email} />
                <Divider sx={{ my: 0.5 }} />
                <InfoRow icon="badge"           label="Role"        value={currentUser.role} />
                <Divider sx={{ my: 0.5 }} />
                <InfoRow icon="corporate_fare"  label="Department"  value={currentUser.department} />
                <Divider sx={{ my: 0.5 }} />
                <InfoRow icon="fingerprint"     label="User ID"
                  value={currentUser.id ? String(currentUser.id).slice(0, 8) + "…" : "—"} />
                <Divider sx={{ my: 0.5 }} />
                <InfoRow icon="business"        label="Club"        value={CLUB_NAME} />
                <Divider sx={{ my: 1.5 }} />
                <SoftBox display="flex" justifyContent="flex-end">
                  <SoftButton
                    variant="outlined" color="info" size="small"
                    startIcon={<Icon>lock</Icon>}
                    onClick={openPwdDialog}
                  >
                    Change Password
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>

          {/* Permissions matrix */}
          <Grid item xs={12} md={7} lg={8}>
            <Card>
              <SoftBox p={3}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <SoftTypography variant="h6" fontWeight="bold">
                    Access Permissions
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text">
                    {grantedCount} granted · {totalCount - grantedCount} restricted
                  </SoftTypography>
                </SoftBox>

                <Grid container spacing={2}>
                  {PERMISSION_SECTIONS.map((section) => {
                    const sectionGranted = section.perms.filter((p) => userPerms.includes(p.key)).length;
                    return (
                      <Grid key={section.label} item xs={12} sm={6}>
                        <SoftBox
                          p={2}
                          borderRadius="lg"
                          sx={{ border: "1px solid #f0f0f0", height: "100%" }}
                        >
                          <SoftBox display="flex" alignItems="center" gap={1} mb={1.5}>
                            <Icon sx={{ color: "info.main", fontSize: "1.1rem !important" }}>
                              {section.icon}
                            </Icon>
                            <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase">
                              {section.label}
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text" ml="auto">
                              {sectionGranted}/{section.perms.length}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox display="flex" flexWrap="wrap" gap={0.5}>
                            {section.perms.map((p) => {
                              const granted = userPerms.includes(p.key);
                              return (
                                <Chip
                                  key={p.key}
                                  label={p.label}
                                  size="small"
                                  icon={
                                    <Icon sx={{ fontSize: "0.75rem !important" }}>
                                      {granted ? "check_circle" : "block"}
                                    </Icon>
                                  }
                                  sx={{
                                    fontSize: "0.65rem",
                                    height: 22,
                                    backgroundColor: granted
                                      ? "rgba(102,187,106,0.12)"
                                      : "rgba(0,0,0,0.05)",
                                    color: granted ? "success.main" : "text.secondary",
                                    "& .MuiChip-icon": {
                                      color: granted ? "success.main" : "text.disabled",
                                    },
                                  }}
                                />
                              );
                            })}
                          </SoftBox>
                        </SoftBox>
                      </Grid>
                    );
                  })}
                </Grid>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>

      <Footer company={{ href: "#", name: CLUB_NAME }} links={[]} />

      {/* ── Change Password Dialog ─────────────────────────────────────────────── */}
      <Dialog open={pwdOpen} onClose={() => setPwdOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Change Password</DialogTitle>
        <DialogContent dividers>
          {pwdSuccess && <Alert severity="success" sx={{ mb: 2 }}>Password changed successfully!</Alert>}
          {pwdError   && <Alert severity="error"   sx={{ mb: 2 }}>{pwdError}</Alert>}
          {otpSent    && !pwdSuccess && (
            <Alert severity="info" sx={{ mb: 2 }}>
              OTP sent to <strong>{currentUser.email}</strong>. Check your inbox.
            </Alert>
          )}
          <SoftBox display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField fullWidth label="Current Password" type="password" value={currentPwd}
              onChange={(e) => { setCurrentPwd(e.target.value); setPwdError(""); }}
              size="small" InputLabelProps={{ shrink: true }} disabled={otpSent} />
            <TextField fullWidth label="New Password" type="password" value={newPwd}
              onChange={(e) => { setNewPwd(e.target.value); setPwdError(""); }}
              size="small" helperText="Min 8 characters" InputLabelProps={{ shrink: true }} disabled={otpSent} />
            <TextField fullWidth label="Confirm New Password" type="password" value={confirmPwd}
              onChange={(e) => { setConfirmPwd(e.target.value); setPwdError(""); }}
              size="small" InputLabelProps={{ shrink: true }} disabled={otpSent} />
            {otpSent && (
              <TextField fullWidth label="Enter OTP" value={otp}
                onChange={(e) => { setOtp(e.target.value); setPwdError(""); }}
                size="small" inputProps={{ maxLength: 6 }} autoFocus
                helperText="6-digit code sent to your email"
                InputLabelProps={{ shrink: true }} />
            )}
          </SoftBox>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setPwdOpen(false)}>Cancel</SoftButton>
          {!otpSent ? (
            <SoftButton variant="gradient" color="info" onClick={handleSendOtp} disabled={otpSending}>
              {otpSending ? "Sending…" : "Send OTP"}
            </SoftButton>
          ) : (
            <SoftButton variant="gradient" color="info" onClick={handleChangePassword} disabled={pwdSaving || pwdSuccess}>
              {pwdSaving ? "Saving…" : "Confirm Change"}
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>

    </DashboardLayout>
  );
}

export default ProfilePage;
